import type { Ref } from 'vue';
import type { FormSaveContext, FormSaveError, FormSaveHandler } from '#layers/forms/public';
import { createEventHook, tryOnScopeDispose } from '@vueuse/core';
import { ref } from 'vue';

export interface UseFormBlockSaveHandlersReturn {
	saveHook: ReturnType<typeof createEventHook<FormSaveContext>>;
	saveHandlerErrors: Ref<FormSaveError[]>;
	onSave: (handler: FormSaveHandler) => void;
	reportHasChanges: (getter: () => boolean) => void;
	hasExternalChanges: () => boolean;
	clearSaveHandlerErrors: () => void;
}

export function useFormBlockSaveHandlers(): UseFormBlockSaveHandlersReturn {
	const saveHook = createEventHook<FormSaveContext>();
	const hasChangesReporters = ref<Set<() => boolean>>(new Set());
	const saveHandlerErrors = ref<FormSaveError[]>([]);

	function onSave(handler: FormSaveHandler) {
		const { off } = saveHook.on(handler);
		tryOnScopeDispose(off);
	}

	function reportHasChanges(getter: () => boolean) {
		hasChangesReporters.value.add(getter);
		tryOnScopeDispose(() => hasChangesReporters.value.delete(getter));
	}

	function hasExternalChanges(): boolean {
		for (const getter of hasChangesReporters.value) {
			if (getter()) return true;
		}

		return false;
	}

	function clearSaveHandlerErrors() {
		saveHandlerErrors.value = [];
	}

	return {
		saveHook,
		saveHandlerErrors,
		onSave,
		reportHasChanges,
		hasExternalChanges,
		clearSaveHandlerErrors,
	};
}
